import { CartAddEvent } from '@theme/events';

class GoldenTicketTreats extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.form = this.closest('form');
    if (!this.form) return;
    this.productForm = this.closest('product-form-component');
    this.status = this.querySelector('.gt-treats__status');
    const options = { signal: this.controller.signal };
    this.form.addEventListener('input', () => this.render(), options);
    this.form.addEventListener('change', () => this.render(), options);
    this.productForm.addEventListener('quantity-selector:update', () => this.render(), options);
    this.hidden = false;
    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  selection() {
    const radio = this.querySelector('input[type="radio"]:checked');
    if (!radio || radio.value === 'none') return null;
    const selected = radio.value === 'single' ? this.querySelector('[data-design]')?.selectedOptions[0] : radio;
    if (!selected || selected.disabled || radio.disabled) return null;
    return { id: selected.dataset.variantId || selected.value, price: Number(selected.dataset.price) };
  }

  quantity() {
    return Number(this.form.querySelector('[name="quantity"]')?.value || 1);
  }

  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', {
      style: 'currency', currency: this.dataset.currency || 'NZD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: cents % 100 ? 2 : 0
    }).format(cents / 100);
  }

  render() {
    const extra = this.selection();
    const single = this.querySelector('input[value="single"]:checked');
    const designWrap = this.querySelector('.gt-treats__design');
    if (designWrap) designWrap.hidden = !single;
    const design = this.querySelector('[data-design]')?.selectedOptions[0];
    if (design) {
      const image = this.querySelector('.gt-treats__single-image');
      image.removeAttribute('srcset');
      image.src = design.dataset.image;
      image.alt = design.dataset.title;
      this.querySelector('[data-single-price]').textContent = '+' + this.money(Number(design.dataset.price));
    }
    const quantity = this.quantity();
    if (!Number.isInteger(quantity) || quantity < 1) return;
    const total = this.money((Number(this.dataset.basePrice) + (extra?.price || 0)) * quantity);
    this.querySelector('[data-total]').textContent = total + ' ' + this.dataset.currency;
    const label = this.form.querySelector('.add-to-cart-text__content');
    if (label && !this.busy && !this.form.querySelector('[name="add"]')?.disabled) {
      label.textContent = this.dataset.addLabel + ' — ' + total;
    }
    const accelerated = this.form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (accelerated) accelerated.hidden = Boolean(extra);
  }

  async submit(event) {
    const extra = this.selection();
    if (!extra && !this.busy) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy) return;
    const submitButton = this.form.querySelector('[name="add"]');
    if (submitButton?.disabled || !this.form.reportValidity()) return;
    const quantity = this.quantity();
    if (!Number.isInteger(quantity) || quantity < 1) return;
    const data = new FormData(this.form);
    const voucher = { id: data.get('id'), quantity };
    if (!voucher.id || !extra?.id) return;
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) voucher.properties = properties;
    if (data.get('selling_plan')) voucher.selling_plan = data.get('selling_plan');
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
    const buttons = [...this.productForm.querySelectorAll('button[type="submit"]')];
    const previousDisabled = buttons.map(button => button.disabled);
    this.busy = true;
    buttons.forEach(button => { button.disabled = true; });
    this.form.setAttribute('aria-busy', 'true');
    this.status.hidden = false;
    this.status.textContent = this.dataset.addingLabel;
    this.querySelector('.gt-treats__review').hidden = true;
    let failed = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [voucher, { id: extra.id, quantity }], sections, sections_url: location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        failed = true;
        this.status.textContent = typeof result.description === 'string' ? result.description + ' ' + this.dataset.errorLabel : this.dataset.errorLabel;
      } else {
        this.status.textContent = this.dataset.successLabel;
      }
      if (!failed) {
        this.dispatchEvent(new CartAddEvent({}, voucher.id.toString(), {
          source: 'golden-ticket-treats', productId: this.productForm.dataset.productId,
          itemCount: quantity * 2, sections: result.sections
        }));
      }
    } catch {
      failed = true;
      this.status.textContent = this.dataset.uncertainLabel;
    } finally {
      this.querySelector('.gt-treats__review').hidden = false;
      buttons.forEach((button, index) => { button.disabled = previousDisabled[index]; });
      this.form.removeAttribute('aria-busy');
      this.busy = false;
      this.render();
      if (failed) this.querySelector('.gt-treats__review').focus();
    }
  }
}

if (!customElements.get('golden-ticket-treats')) customElements.define('golden-ticket-treats', GoldenTicketTreats);
